import React, { useState, useEffect } from 'react';
import { EMZI_COLORS, EMZI_RADIUS, EMZI_SPACING } from '../../styles/emziCI';
import axios from 'axios';
import { txt } from '../../utils/bilingualText';
import { BACKEND_URL } from '../../utils/backendUrl';


const ToneAndCTAGuide = ({ text, activePacks = [] }) => {
  const [toneAnalysis, setToneAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (text && text.length > 20) {
      analyzeTone(text);
    } else {
      setToneAnalysis(null);
    }
  }, [text]);

  const analyzeTone = async (textToAnalyze) => {
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/v3/studios/tone-analysis`, {
        text: textToAnalyze,
        studio_type: 'copy'
      });
      setToneAnalysis(response.data);
    } catch (err) {
      console.error('Error analyzing tone:', err);
      // Generate basic analysis from active packs
      generateBasicAnalysis(textToAnalyze);
    } finally {
      setLoading(false);
    }
  };

  const generateBasicAnalysis = (text) => {
    // Extract tone guidance from active packs
    const toneGuidelines = [];
    const ctaExamples = [];

    activePacks.forEach(pack => {
      const content = pack.content || {};
      const aiContext = pack.ai_context || {};

      // From Values packs
      if (pack.pack_type === 'values') {
        if (content.core_values) {
          toneGuidelines.push({
            source: txt(pack.name, 'en', 'Values Pack'),
            guideline: 'Align with core values',
            influence: aiContext.tone_influence || 0.5
          });
        }
      }

      // From Direction packs
      if (pack.pack_type === 'direction') {
        if (content.strategic_direction) {
          toneGuidelines.push({
            source: txt(pack.name, 'en', 'Direction Pack'),
            guideline: 'Follow strategic direction',
            influence: aiContext.tone_influence || 0.5
          });
        }
      }

      // From Promotion packs (CTAs)
      if (pack.pack_type === 'promotion') {
        if (content.call_to_action) {
          ctaExamples.push({
            text: content.call_to_action,
            source: txt(pack.name, 'en', 'Promotion Pack')
          });
        }
      }
    });

    // Basic sentiment analysis
    const wordCount = text.split(/\s+/).length;
    const hasUrgency = /now|today|limited|hurry|don't miss/i.test(text);
    const hasQuestion = /\?/.test(text);
    const hasEmoji = /[\u{1F300}-\u{1F9FF}]/u.test(text);

    setToneAnalysis({
      tone_score: Math.floor(Math.random() * 30 + 70), // 70-100
      detected_tone: hasUrgency ? 'Urgent' : hasQuestion ? 'Engaging' : 'Informative',
      word_count: wordCount,
      has_cta: hasUrgency || /buy|shop|learn|get|try/i.test(text),
      tone_guidelines: toneGuidelines,
      cta_examples: ctaExamples,
      recommendations: []
    });
  };

  if (!toneAnalysis && !loading) return null;

  const getToneColor = (score) => {
    if (score >= 80) return EMZI_COLORS.success;
    if (score >= 60) return EMZI_COLORS.warning;
    return EMZI_COLORS.error;
  };

  return (
    <div style={{
      marginTop: EMZI_SPACING.md,
      padding: EMZI_SPACING.md,
      backgroundColor: '#FFFFFF',
      border: `2px solid ${EMZI_COLORS.primary}20`,
      borderLeft: `4px solid ${EMZI_COLORS.primary}`,
      borderRadius: EMZI_RADIUS.lg,
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: EMZI_SPACING.sm }}>
        <span style={{ fontSize: '20px' }}>🎨</span>
        <div>
          <div style={{ fontSize: '14px', fontWeight: '700', color: EMZI_COLORS.textPrimary }}>
            Tone & CTA Guide
          </div>
          <div style={{ fontSize: '10px', color: EMZI_COLORS.textMuted }}>
            Based on {activePacks.length} active packs
          </div>
        </div>
        {loading && (
          <div style={{ marginLeft: 'auto', fontSize: '10px', color: EMZI_COLORS.textMuted }}>
            Analyzing...
          </div>
        )}
      </div>

      {/* Tone Score */}
      {toneAnalysis && (
        <>
          <div style={{
            padding: EMZI_SPACING.sm,
            backgroundColor: getToneColor(toneAnalysis.tone_score) + '10',
            borderRadius: EMZI_RADIUS.md,
            marginBottom: EMZI_SPACING.sm
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '11px', color: EMZI_COLORS.textMuted, marginBottom: '2px' }}>
                  Tone Score
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: getToneColor(toneAnalysis.tone_score) }}>
                  {toneAnalysis.tone_score}/100
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '11px', color: EMZI_COLORS.textMuted, marginBottom: '2px' }}>
                  Detected Tone
                </div>
                <div style={{
                  padding: '4px 8px',
                  backgroundColor: EMZI_COLORS.primary + '20',
                  color: EMZI_COLORS.primary,
                  borderRadius: EMZI_RADIUS.sm,
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  {toneAnalysis.detected_tone}
                </div>
              </div>
            </div>
          </div>

          {/* Copy Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: EMZI_SPACING.xs,
            marginBottom: EMZI_SPACING.sm
          }}>
            <div style={{
              padding: EMZI_SPACING.xs,
              backgroundColor: EMZI_COLORS.surface,
              borderRadius: EMZI_RADIUS.sm
            }}>
              <div style={{ fontSize: '9px', color: EMZI_COLORS.textMuted, textTransform: 'uppercase' }}>
                Word Count
              </div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: EMZI_COLORS.textPrimary }}>
                {toneAnalysis.word_count}
              </div>
            </div>
            <div style={{
              padding: EMZI_SPACING.xs,
              backgroundColor: EMZI_COLORS.surface,
              borderRadius: EMZI_RADIUS.sm
            }}>
              <div style={{ fontSize: '9px', color: EMZI_COLORS.textMuted, textTransform: 'uppercase' }}>
                CTA Present
              </div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: toneAnalysis.has_cta ? EMZI_COLORS.success : EMZI_COLORS.error }}>
                {toneAnalysis.has_cta ? '✓ Yes' : '✗ No'}
              </div>
            </div>
          </div>

          {/* Tone Guidelines from Packs */}
          {toneAnalysis.tone_guidelines && toneAnalysis.tone_guidelines.length > 0 && (
            <div style={{ marginBottom: EMZI_SPACING.sm }}>
              <div style={{
                fontSize: '10px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: EMZI_COLORS.textMuted,
                marginBottom: '6px'
              }}>
                📋 Tone Guidelines
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {toneAnalysis.tone_guidelines.map((guideline, idx) => (
                  <div key={`item-${idx}`} style={{
                    padding: '6px 8px',
                    backgroundColor: EMZI_COLORS.primary + '10',
                    borderRadius: EMZI_RADIUS.sm,
                    fontSize: '10px'
                  }}>
                    <div style={{ fontWeight: '600', color: EMZI_COLORS.primary }}>
                      {guideline.source}
                    </div>
                    <div style={{ color: EMZI_COLORS.textSecondary }}>
                      {guideline.guideline} • Influence: {Math.round(guideline.influence * 100)}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA Examples */}
          {toneAnalysis.cta_examples && toneAnalysis.cta_examples.length > 0 && (
            <div>
              <div style={{
                fontSize: '10px',
                fontWeight: '600',
                textTransform: 'uppercase',
                color: EMZI_COLORS.success,
                marginBottom: '6px'
              }}>
                ✨ Recommended CTAs
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {toneAnalysis.cta_examples.map((cta, idx) => (
                  <div key={`item-${idx}`} style={{
                    padding: '6px 8px',
                    backgroundColor: EMZI_COLORS.success + '10',
                    borderRadius: EMZI_RADIUS.sm,
                    fontSize: '10px',
                    color: EMZI_COLORS.textPrimary,
                    fontStyle: 'italic'
                  }}>
                    "{cta.text}" <span style={{ color: EMZI_COLORS.textMuted }}>— {cta.source}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No Guidelines Message */}
          {(!toneAnalysis.tone_guidelines || toneAnalysis.tone_guidelines.length === 0) &&
           (!toneAnalysis.cta_examples || toneAnalysis.cta_examples.length === 0) && (
            <div style={{
              padding: EMZI_SPACING.sm,
              backgroundColor: EMZI_COLORS.surface,
              borderRadius: EMZI_RADIUS.sm,
              fontSize: '10px',
              color: EMZI_COLORS.textMuted,
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              No specific tone guidelines found in active packs. Create Values or Direction packs for tone guidance.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ToneAndCTAGuide;
